import {
  PublishingDocument,
  PublishingGeometry,
  PublishingPage,
  PublishingSite,
  publishingDocumentSchema,
  validatePublishingReferences,
} from "./document";
import { resolveDigitalPresentation } from "./digital-presentation";
import { resolveMasterAssignment } from "./masters";

export type PageRectangle = {
  x: number;
  y: number;
  width: number;
  height: number;
};
export type ResolvedPublishingPage<C = PublishingSite["components"][number]> = {
  id: string;
  number: number;
  side: "left" | "right";
  component: C | null;
  geometry: PublishingGeometry;
  printState: PublishingPage["printState"];
  masterItems?: {
    id: string;
    component: C;
    position: { x: number; y: number };
    layer: "below" | "above";
    printState: PublishingPage["printState"];
  }[];
  boxes: {
    media: PageRectangle;
    trim: PageRectangle;
    bleed: PageRectangle;
    content: PageRectangle;
  };
};

export function resolvePageBoxes(
  geometry: PublishingGeometry,
  side: "left" | "right",
) {
  const { trim, bleed, slug, margins } = geometry;
  const x = slug.left + bleed.left,
    y = slug.top + bleed.top;
  const marginLeft = side === "left" ? margins.outer : margins.inner;
  const marginRight = side === "left" ? margins.inner : margins.outer;
  return {
    media: {
      x: 0,
      y: 0,
      width: x + trim.width + bleed.right + slug.right,
      height: y + trim.height + bleed.bottom + slug.bottom,
    },
    trim: { x, y, ...trim },
    bleed: {
      x: slug.left,
      y: slug.top,
      width: trim.width + bleed.left + bleed.right,
      height: trim.height + bleed.top + bleed.bottom,
    },
    content: {
      x: x + marginLeft,
      y: y + margins.top,
      width: trim.width - marginLeft - marginRight,
      height: trim.height - margins.top - margins.bottom,
    },
  };
}

/** Pure composition: no ViewCtx, frame placement, active variant or mounted DOM dependency. */
export function resolvePublishingComposition<S extends PublishingSite>(
  site: S,
  input: PublishingDocument,
) {
  const doc = publishingDocumentSchema.parse(input);
  validatePublishingReferences(site, doc);
  type C = S["components"][number];
  const pages: ResolvedPublishingPage<C>[] = doc.pages.map((page, index) => {
    const first = doc.binding.firstSide;
    const side =
      !doc.binding.facing || index % 2 === 0
        ? first
        : first === "left"
          ? "right"
          : "left";
    const master = resolveMasterAssignment(doc, page);
    const geometry = master.geometry;
    return {
      id: page.id,
      number: doc.binding.startNumber + index,
      side,
      component:
        page.componentId === null
          ? null
          : (site.components.find((c) => c.uuid === page.componentId)! as C),
      geometry,
      ...(page.masterId
        ? {
            masterItems: master.items
              .filter((item) => item.side === "both" || item.side === side)
              .map((item) => ({
                id: item.id,
                component: site.components.find(
                  (c) => c.uuid === item.componentId,
                )! as C,
                position: item.position,
                layer: item.layer,
                printState: item.printState,
              })),
          }
        : {}),
      printState: page.printState,
      boxes: resolvePageBoxes(geometry, side),
    };
  });
  const spreads: {
    id: string;
    left?: ResolvedPublishingPage<C>;
    right?: ResolvedPublishingPage<C>;
  }[] = [];
  let current: (typeof spreads)[number] | undefined;
  for (const page of pages) {
    const isFirstSide =
      page.side === (doc.binding.direction === "ltr" ? "left" : "right");
    if (!doc.binding.facing || !current || isFirstSide) {
      current = { id: `spread-${page.id}` };
      spreads.push(current);
    }
    current[page.side] = page;
  }
  return {
    version: 2 as const,
    title: doc.title,
    pages,
    spreads,
    /** Reader policy only; page geometry above stays print-owned. */
    digital: { presentation: resolveDigitalPresentation(doc) },
  };
}

export function hasPublishingPageContent(
  page: Pick<ResolvedPublishingPage, "component" | "masterItems">,
) {
  return !!page.component || !!page.masterItems?.length;
}
